import { useEffect, useState } from "react";
import { AppLayout } from "../components/layout/AppLayout";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { RadarChart } from "../components/ui/RadarChart";
import { api } from "../lib/api";
import type { PersonaProfile, VaultItem } from "../types";

const inputClass =
  "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-navy placeholder:text-slate-300 focus:border-teal focus:outline-none";

const VAULT_TYPE_LABEL: Record<VaultItem["type"], string> = {
  work: "Work Experience",
  education: "Education",
  project: "Project",
  certification: "Certification",
};

export default function PersonaCard() {
  const [persona, setPersona] = useState<PersonaProfile | null>(null);
  const [vault, setVault] = useState<VaultItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddItem, setShowAddItem] = useState(false);

  const [form, setForm] = useState({
    headline: "",
    bio: "",
    location: "",
    remotePreference: "flexible",
    skillsInput: "",
    careerGoal: "",
  });

  async function load() {
    setLoading(true);
    try {
      const [p, v] = await Promise.all([
        api.get<PersonaProfile>("/api/persona"),
        api.get<{ items: VaultItem[] }>("/api/persona/vault"),
      ]);
      setPersona(p);
      setVault(v.items);
      setForm({
        headline: p.headline ?? "",
        bio: p.bio ?? "",
        location: p.location ?? "",
        remotePreference: p.remotePreference ?? "flexible",
        skillsInput: p.skills.join(", "),
        careerGoal: p.careerGoal ?? "",
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load data.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function saveProfile() {
    setSaving(true);
    setError(null);
    try {
      const updated = await api.put<PersonaProfile>("/api/persona", {
        headline: form.headline,
        bio: form.bio,
        location: form.location,
        remotePreference: form.remotePreference,
        careerGoal: form.careerGoal,
        skills: form.skillsInput.split(",").map((s) => s.trim()).filter(Boolean),
        teamFitTraits: persona?.teamFitTraits,
        workStyle: persona?.workStyle,
      });
      setPersona(updated);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save.");
    } finally {
      setSaving(false);
    }
  }

  async function deleteItem(id: string) {
    await api.delete(`/api/persona/vault/${id}`);
    setVault((v) => v.filter((item) => item.id !== id));
  }

  if (loading) return <AppLayout title="My Persona">{null}</AppLayout>;

  return (
    <AppLayout title="My Persona">
      {error && <Card className="mb-3 border border-coral/30 bg-coral/5 text-xs text-coral">{error}</Card>}

      <Card className="border border-slate-100">
        {!editing ? (
          <>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-base font-bold text-navy">{persona?.headline || "No headline set"}</p>
                <p className="text-xs text-slate-400">{persona?.location || "Location not set"}</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                Edit
              </Button>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">{persona?.bio || "Write your bio to introduce yourself."}</p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {(persona?.skills ?? []).map((s) => (
                <span key={s} className="rounded-full bg-slate-50 px-2 py-1 text-[11px] text-slate-500">
                  {s}
                </span>
              ))}
            </div>
          </>
        ) : (
          <div>
            <Field label="Headline">
              <input className={inputClass} value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} />
            </Field>
            <Field label="Location">
              <input className={inputClass} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
            </Field>
            <Field label="Work Preference">
              <select className={inputClass} value={form.remotePreference} onChange={(e) => setForm({ ...form, remotePreference: e.target.value })}>
                <option value="remote">Remote</option>
                <option value="hybrid">Hybrid</option>
                <option value="onsite">On-site</option>
                <option value="flexible">Flexible</option>
              </select>
            </Field>
            <Field label="Bio">
              <textarea className={inputClass} rows={3} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} />
            </Field>
            <Field label="Skills (comma separated)">
              <input className={inputClass} value={form.skillsInput} onChange={(e) => setForm({ ...form, skillsInput: e.target.value })} />
            </Field>
            <Field label="Career Goal">
              <input className={inputClass} value={form.careerGoal} onChange={(e) => setForm({ ...form, careerGoal: e.target.value })} />
            </Field>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditing(false)} className="flex-shrink-0">
                Cancel
              </Button>
              <Button fullWidth onClick={saveProfile} loading={saving}>
                Save
              </Button>
            </div>
          </div>
        )}
      </Card>

      <Card className="mt-3 border border-slate-100">
        <p className="text-sm font-semibold text-navy">Team-fit Radar</p>
        <div className="mt-2 flex justify-center">
          <RadarChart
            traits={
              persona?.teamFitTraits ?? {
                communication: 0,
                collaboration: 0,
                leadership: 0,
                problemSolving: 0,
                adaptability: 0,
                integrity: 0,
              }
            }
            size={240}
          />
        </div>
      </Card>

      <div className="mt-5 flex items-center justify-between">
        <p className="text-sm font-semibold text-navy">Master Career Vault</p>
        <Button size="sm" variant="secondary" onClick={() => setShowAddItem((s) => !s)}>
          {showAddItem ? "Close" : "+ Add Item"}
        </Button>
      </div>

      {showAddItem && (
        <AddVaultItemForm
          onCreated={(item) => {
            setVault((v) => [item, ...v]);
            setShowAddItem(false);
          }}
        />
      )}

      <div className="mt-3 space-y-2">
        {vault.length === 0 && (
          <Card className="border border-dashed border-slate-200 text-center text-xs text-slate-400">
            No career entries yet. Add your experience to power AI resume generation.
          </Card>
        )}
        {vault.map((item) => (
          <Card key={item.id} className="border border-slate-100">
            <div className="flex items-start justify-between">
              <div>
                <span className="rounded-full bg-slate-50 px-2 py-0.5 text-[10px] text-slate-500">
                  {VAULT_TYPE_LABEL[item.type]}
                </span>
                <p className="mt-1 text-sm font-semibold text-navy">{item.title}</p>
                <p className="text-xs text-slate-400">
                  {item.organization} · {item.start_date ?? "?"} ~ {item.is_current ? "Present" : item.end_date ?? "?"}
                </p>
              </div>
              <button onClick={() => deleteItem(item.id)} className="text-xs text-slate-300 active:text-coral">
                Delete
              </button>
            </div>
            {item.description && <p className="mt-2 text-xs leading-relaxed text-slate-500">{item.description}</p>}
          </Card>
        ))}
      </div>
    </AppLayout>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="mb-3 block">
      <span className="mb-1 block text-xs font-medium text-slate-500">{label}</span>
      {children}
    </label>
  );
}

function AddVaultItemForm({ onCreated }: { onCreated: (item: VaultItem) => void }) {
  const [type, setType] = useState<VaultItem["type"]>("work");
  const [title, setTitle] = useState("");
  const [organization, setOrganization] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isCurrent, setIsCurrent] = useState(false);
  const [description, setDescription] = useState("");
  const [skillsInput, setSkillsInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!title.trim()) {
      setError("Title is required.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const skills = skillsInput.split(",").map((s) => s.trim()).filter(Boolean);
      const res = await api.post<{ id: string }>("/api/persona/vault", {
        type,
        title,
        organization,
        startDate,
        endDate: isCurrent ? undefined : endDate,
        isCurrent,
        description,
        skills,
      });
      onCreated({
        id: res.id,
        user_id: "",
        type,
        title,
        organization: organization || null,
        start_date: startDate || null,
        end_date: isCurrent ? null : endDate || null,
        is_current: isCurrent ? 1 : 0,
        description: description || null,
        skills,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="mt-3 border border-slate-100">
      <Field label="Type">
        <select className={inputClass} value={type} onChange={(e) => setType(e.target.value as VaultItem["type"])}>
          <option value="work">Work Experience</option>
          <option value="education">Education</option>
          <option value="project">Project</option>
          <option value="certification">Certification</option>
        </select>
      </Field>
      <Field label="Title">
        <input className={inputClass} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Senior Product Designer" />
      </Field>
      <Field label="Organization">
        <input className={inputClass} value={organization} onChange={(e) => setOrganization(e.target.value)} />
      </Field>
      <div className="mb-3 grid grid-cols-2 gap-2">
        <Field label="Start Date">
          <input type="month" className={inputClass} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </Field>
        <Field label="End Date">
          <input type="month" className={inputClass} value={endDate} disabled={isCurrent} onChange={(e) => setEndDate(e.target.value)} />
        </Field>
      </div>
      <label className="mb-3 flex items-center gap-2 text-xs text-slate-500">
        <input type="checkbox" checked={isCurrent} onChange={(e) => setIsCurrent(e.target.checked)} />
        Currently active
      </label>
      <Field label="Description">
        <textarea className={inputClass} rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
      </Field>
      <Field label="Related Skills (comma separated)">
        <input className={inputClass} value={skillsInput} onChange={(e) => setSkillsInput(e.target.value)} />
      </Field>
      {error && <p className="mb-2 text-xs text-coral">{error}</p>}
      <Button fullWidth onClick={submit} loading={saving}>
        Save
      </Button>
    </Card>
  );
}
